import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Eye, EyeOff, ArrowLeft } from "lucide-react"
import { useAuth } from "../contexts/AuthContext"
import LocationPicker from "./LocationPicker"
import PageMeta from "./PageMeta"

function LoginPage() {
  const navigate = useNavigate()
  const { user, profile, signIn, signUp, signInWithGoogle, loading } = useAuth()

  // If already logged in, redirect away
  useEffect(() => {
    if (loading) return
    if (user && profile?.username) {
      navigate('/', { replace: true })
    } else if (user && !profile?.username) {
      navigate('/profile-setup', { replace: true })
    }
  }, [user, profile, loading, navigate])
  
  const [isLogin, setIsLogin] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    username: "",
    country: "",
    city: "",
  })
  const [locationData, setLocationData] = useState({})
  const [errors, setErrors] = useState({})

  const validateEmail = (email) => {
    return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
  }

  const validateUsername = (username) => {
    return username.match(/^[a-zA-Z0-9_]{3,20}$/)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })

    // Clear errors when typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" })
    }
  }

  const handleLocationChange = (location) => {
    setLocationData(location)

    // Update formData for backward compatibility with existing validation
    setFormData(prev => ({
      ...prev,
      country: location.country_name || "",
      city: location.city_locality || "",
    }))

    // Clear location-related errors
    if (errors.country) setErrors(prev => ({ ...prev, country: "" }))
    if (errors.city) setErrors(prev => ({ ...prev, city: "" }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (isLoading) return // Prevent multiple submissions
    
    setIsLoading(true)
    setErrors({}) // Clear any previous errors

    const newErrors = {}

    // Validation
    if (!isLogin && !formData.name) newErrors.name = "Name is required"
    if (!isLogin && !formData.username) newErrors.username = "Username is required"
    if (!isLogin && formData.username && !validateUsername(formData.username)) {
      newErrors.username = "Username must be 3-20 characters (letters, numbers, underscore only)"
    }
    if (!isLogin && !locationData.city_locality) newErrors.city = "City is required"
    if (!formData.email) newErrors.email = "Email is required"
    else if (!validateEmail(formData.email)) newErrors.email = "Please enter a valid email"
    if (!formData.password) newErrors.password = "Password is required"
    else if (formData.password.length < 6) newErrors.password = "Password must be at least 6 characters"

    setErrors(newErrors)

    if (Object.keys(newErrors).length > 0) {
      setIsLoading(false)
      return
    }

    try {
      if (isLogin) {
        const { error } = await signIn(formData.email, formData.password)
        
        if (error) {
          setErrors({ submit: error.message })
          return
        }
        
        // Check for redirect URL after successful login
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin')
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin')
          navigate(redirectUrl)
        } else {
          navigate('/') // Redirect to home after successful login
        }
      } else {
        const userData = {
          username: formData.username,
          display_name: formData.name,
          ...locationData, // Include all location data
        }
        
        const { error } = await signUp(formData.email, formData.password, userData)
        
        if (error) {
          setErrors({ submit: error.message })
          return
        }
        
        // Show success message for signup
        setErrors({ submit: "Check your email to confirm your account!" })
      }
    } catch (error) {
      setErrors({ submit: error.message })
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    if (isLoading) return // Prevent multiple clicks
    
    setIsLoading(true)
    setErrors({}) // Clear any previous errors
    
    try {
      const { error } = await signInWithGoogle()
      
      if (error) {
        setErrors({ submit: error.message })
      }
    } catch (error) {
      setErrors({ submit: error.message })
    } finally {
      setIsLoading(false)
    }
  }

  const toggleView = () => {
    setIsLogin(!isLogin)
    setErrors({})
    setFormData({
      name: "",
      email: "",
      password: "",
      username: "",
      country: "",
      city: "",
    })
    setLocationData({})
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark p-4">
      <PageMeta title="Log in | Lilycove" description="Log in or create your Lilycove account." />
      {/* Back arrow */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-6 left-6 flex items-center justify-center w-10 h-10 text-cream hover:text-purple hover:bg-dark/50 rounded-lg transition-colors"
      >
        <ArrowLeft size={20} />
      </button>

      <div className="w-full max-w-[400px] bg-dark rounded-lg shadow-lg p-6 border border-purple/30">
        <div className="text-center mb-6">
          <h1 className="text-cream text-2xl font-bold">{isLogin ? "Welcome Back" : "Create Account"}</h1>
          <p className="text-cream/70 text-sm mt-1">
            {isLogin ? "Sign in to access your account" : "Join the Pokémon card community"}
          </p>
        </div>

        {errors.submit && (
          <div className={`p-3 rounded-lg text-sm mb-4 ${
            errors.submit.includes('Check your email') 
              ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
              : 'bg-red-500/10 text-red-400 border border-red-500/20'
          }`}>
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {!isLogin && (
            <>
              <div className="space-y-2">
                <label htmlFor="name" className="text-cream text-sm block">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple placeholder:text-cream/50"
                />
                {errors.name && <p className="text-purple text-xs mt-1">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <label htmlFor="username" className="text-cream text-sm block">
                  Username
                </label>
                <input
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Choose a username"
                  className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple placeholder:text-cream/50"
                />
                {errors.username && <p className="text-purple text-xs mt-1">{errors.username}</p>}
              </div>

              <div className="space-y-4">
                <LocationPicker
                  value={locationData}
                  onChange={handleLocationChange}
                />
                {errors.city && <p className="text-purple text-xs mt-1">{errors.city}</p>}
              </div>
            </>
          )}

          <div className="space-y-2">
            <label htmlFor="email" className="text-cream text-sm block">
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple placeholder:text-cream/50"
            />
            {errors.email && <p className="text-purple text-xs mt-1">{errors.email}</p>}
          </div>

          <div className="space-y-2">
            <div className="flex justify-between">
              <label htmlFor="password" className="text-cream text-sm">
                Password
              </label>
              {isLogin && (
                <button type="button" className="text-cream text-xs hover:text-purple transition-colors">
                  Forgot Password?
                </button>
              )}
            </div>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-purple focus:border-purple placeholder:text-cream/50"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-cream hover:text-purple transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && <p className="text-purple text-xs mt-1">{errors.password}</p>}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-purple text-cream hover:bg-purple/80 transition-colors py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Loading..." : (isLogin ? "Sign In" : "Create Account")}
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-purple/20" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-dark px-2 text-cream/50">Or continue with</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full bg-white text-gray-900 hover:bg-gray-100 transition-colors py-2 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign {isLogin ? "in" : "up"} with Google
          </button>

          <div className="text-center mt-4">
            <button
              type="button"
              onClick={toggleView}
              className="text-cream text-sm hover:text-purple transition-colors"
            >
              {isLogin ? "Don't have an account? Sign Up" : "Already have an account? Sign In"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LoginPage
